import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/Reducers';
import useGlobals from './useGlobals';
import { SocketContext } from '../SocketContext';
import { ChatList } from '../Types';
import { setChatLoaded, setIsNewChat, setIsSending } from '../Store/Actions/chatActions';
import { setChatlist } from '../Store/Actions/chatlistActions';
import { chatService } from '../Services/chatService';
import Store from '../Store/Store';
import { setNavigationSubTitle, setNavigationTitle } from '../Store/Actions/navigationHeaderActions';

const useSocketMessageHandler = () => {
    const { handleNavigate, dispatch, userSettings } = useGlobals();
    const { socket } = useContext(SocketContext);

    const isSending = useSelector((state: RootState) => state.chats.isSending);
    const isStopable = useSelector((state: RootState) => state.chats.isStopable);
    const selectedLanguage = useSelector((state: RootState) => state.i18n.selectedLanguage);

    const sendSocketMessage = useCallback(
        async (
            chatIdentifier: string = '',
            message: string = '',
            dlpActive: boolean = true,
            images: any[] = [{}],
            files: any[] = [{}],
            callbacks: Array<() => void> = [],
        ) => {
            const params = {
                userContent: message,
                type: 'sendMessage',
                chatIdentifier,
                dlpActive,
                images,
                files,
                language: selectedLanguage,
            };

            socket?.send(JSON.stringify(params));
            callbacks.forEach((callback) => callback());
        },
        [socket, selectedLanguage],
    );

    const sendMessage = useCallback(
        async (
            chatId: string = '',
            message: string = '',
            promptIdentifier: string = '',
            chats: ChatList[],
            dlpActive: boolean = true,
            images: any[] = [{}],
            files: any[] = [{}],
            callbacks: Array<() => void> = [],
        ) => {
            if (isSending || !message) return;

            dispatch(setIsSending(true));

            if (!chatId) {
                try {
                    const response = await chatService.createChat(promptIdentifier, dlpActive);

                    const chatData = {
                        assistantTitle: response.data.prompt_name,
                        assistantImage: response.data.prompt_image,
                        ...response.data,
                    };

                    dispatch(setNavigationSubTitle(''));
                    dispatch(setNavigationTitle(''));
                    dispatch(setIsNewChat(true));
                    dispatch(setChatLoaded(false));
                    dispatch(setChatlist([chatData, ...chats]));
                    handleNavigate(`/chats/${response.data.identifier}`);
                    while (!Store.getState().chats.chatLoaded) {
                        await new Promise((resolve) => setTimeout(resolve, 5));
                    }
                    await sendSocketMessage(response.data.identifier, message, dlpActive, images, files, callbacks);
                } catch (error) {
                    console.error('Error creating chat:', error);
                }
            } else {
                while (!Store.getState().chats.chatLoaded) {
                    await new Promise((resolve) => setTimeout(resolve, 5));
                }
                await sendSocketMessage(chatId, message, dlpActive, images, files, callbacks);
            }
        },
        [isSending, dispatch, handleNavigate, sendSocketMessage],
    );

    const stopStream = useCallback(async () => {
        if (!isSending && isStopable) {
            return;
        }

        let params = {
            email: userSettings.email ?? '',
            type: 'stopStream',
            language: selectedLanguage,
        };

        dispatch(setIsSending(false));

        socket?.send(JSON.stringify(params));
    }, [isSending, isStopable, userSettings.email, selectedLanguage, socket, dispatch]);

    const checkFiles = useCallback(
        async (sha1Files: string[]) => {
            let params = {
                email: userSettings.email ?? '',
                sha1Files: sha1Files,
                type: 'checkFiles',
                language: selectedLanguage,
            };

            dispatch(setIsSending(false));

            socket?.send(JSON.stringify(params));
        },
        [socket, dispatch, selectedLanguage, userSettings.email],
    );

    const SocketMessageHandlerActions = useMemo(
        () => ({
            sendSocketMessage,
            sendMessage,
            stopStream,
            checkFiles,
        }),
        [sendSocketMessage, sendMessage, stopStream, checkFiles],
    );

    return { SocketMessageHandlerActions };
};

export default useSocketMessageHandler;
